// src/stores/usePadStore.js
//
// Pad reclamation selection state + access road curation.
//
// Access roads now live as an in-session selection list rather than a
// read-only server response. Each entry is either:
//   - source: 'detected' — came from the auto-detect query on the server
//   - source: 'user'     — drawn by the user via MlDraw
//
// Each entry has an `included` flag that drives volume math and map style.
// When the user switches pads, all in-session state is reset — the server
// response for the newly-selected pad becomes the fresh source of truth.
//
// DB persistence is not implemented in this step. When we add it:
// - server endpoint returns merged selections (persisted + newly detected)
// - toggle/draw/remove actions hit the server
// - the store shape stays essentially identical

import { create } from "zustand";
import length from "@turf/length";
import { computeUniformVolumes } from "../utils/padVolumetrics";

const DEFAULT_ROAD_WIDTH_FT = 18;

let userRoadCounter = 0;

export const usePadStore = create((set, get) => ({

  /* ---------------- STATE ---------------- */

  hoveredPadId: null,

  // Active PadRekko panel tab ("qc" | "volumetrics"). Lifted out of
  // PadRekkoPanel's local state so MapPage can gate the full-bleed QC project
  // dashboard on it, and the dashboard can hand control back to Volumetrics.
  // Pad clicks never touch this — the active tab stays independent of
  // selection (same contract the old local state had).
  padrekkoTab: "qc",
  setPadrekkoTab(tab) {
    set({ padrekkoTab: tab });
  },

  selectedPad: null,
  // { id, name, properties, geom }

  // Flat list of road selection entries for the CURRENTLY selected pad.
  // Each entry:
  //   {
  //     key:       string   — stable id within this session ('detected:<uuid>' | 'user:<n>')
  //     source:    'detected' | 'user'
  //     roadId:    uuid      — only when source='detected'
  //     geom:      GeoJSON LineString
  //     length_m:  number
  //     included:  boolean
  //   }
  roadSelections: [],

  loadingRoads: false,
  roadsError: null,

  // Transient UI hint — when user clicks a road on the map, set this to
  // its key so the panel chip can pulse briefly. Cleared after ~800ms.
  pulseRoadKey: null,

  // Drawing mode flag — when true, MlDraw is active in LineString mode and
  // the pad-click/road-click handlers on the map are suspended.
  drawingRoad: false,

  // Assumed road width for volume estimates (ft)
  roadWidthFt: DEFAULT_ROAD_WIDTH_FT,

  /* ---------------- PAD SELECTION ---------------- */

  setHoveredPad(id) {
    set({ hoveredPadId: id ?? null });
  },

  async selectPad({ id, properties, geom }) {
    if (!id || !geom) return;

    set({
      selectedPad: {
        id,
        name: properties?.name ?? properties?.pad_name ?? `Pad ${String(id).slice(0, 8)}`,
        properties: properties ?? {},
        geom,
      },
      roadSelections: [],
      roadsError: null,
      loadingRoads: false,
      drawingRoad: false,
      pulseRoadKey: null,
    });
  },

  clearSelection() {
    set({
      selectedPad: null,
      hoveredPadId: null,
      roadSelections: [],
      roadsError: null,
      loadingRoads: false,
      drawingRoad: false,
      pulseRoadKey: null,
    });
  },

  /* ---------------- ROAD CURATION ---------------- */

  toggleRoadInclusion(key) {
    const list = get().roadSelections;
    const next = list.map((r) =>
      r.key === key ? { ...r, included: !r.included } : r
    );
    set({ roadSelections: next });
  },

  removeUserRoad(key) {
    const list = get().roadSelections;
    // Only user-drawn roads can be deleted; detected roads are toggled off
    if (!list.find((r) => r.key === key && r.source === "user")) return;
    set({ roadSelections: list.filter((r) => r.key !== key) });
  },

  addUserRoad(geom) {
    if (!geom || geom.type !== "LineString") return;

    const meters = length(
      { type: "Feature", geometry: geom, properties: {} },
      { units: "meters" }
    );

    userRoadCounter += 1;
    const entry = {
      key: `user:${userRoadCounter}`,
      source: "user",
      roadId: null,
      geom,
      length_m: meters,
      included: true,
    };

    set({ roadSelections: [...get().roadSelections, entry] });
    return entry.key;
  },

  pulseRoad(key) {
    set({ pulseRoadKey: key });
    // Panel component reads this flag; it's cleared via setTimeout
    // rather than store setTimeout so the store stays pure sync.
    if (typeof window !== "undefined") {
      setTimeout(() => {
        if (get().pulseRoadKey === key) set({ pulseRoadKey: null });
      }, 800);
    }
  },

  /* ---------------- DRAWING MODE ---------------- */

  setDrawingRoad(active) {
    set({ drawingRoad: !!active });
  },

  /* ---------------- WIDTH ---------------- */

  setRoadWidthFt(ft) {
    const n = Number(ft);
    if (!Number.isFinite(n) || n <= 0) return;
    set({ roadWidthFt: n });
  },

  /* ---------------- DERIVED ---------------- */

  getVolumetrics() {
    const pad = get().selectedPad;
    if (!pad?.geom) return null;
    return computeUniformVolumes(pad.geom);
  },

  // Total length of INCLUDED roads only. Used for road volumetrics and the
  // panel summary.
  getIncludedRoadsTotalLength() {
    return get().roadSelections
      .filter((r) => r.included)
      .reduce((sum, r) => sum + r.length_m, 0);
  },
}));
